import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';

const FreeAssessmentPage = () => {
  const navigate = useNavigate();
  const [childName, setChildName] = useState('');
  const [childsAgeInMonths, setChildsAgeInMonths] = useState('');
  const [childGender, setChildGender] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [ageError, setAgeError] = useState('');
  const [mobileError, setMobileError] = useState('');

  const handleFormSubmit = (e) => { 
    e.preventDefault(); 
    
    const ageNum = parseInt(childsAgeInMonths, 10);
    if (isNaN(ageNum) || ageNum < 16 || ageNum > 30) {
      setAgeError('Age must be between 16 and 30 months.');
      return;
    }
    setAgeError('');

    if (!childName.trim()) {
      alert("Please enter the child's name.");
      return;
    }
    if (!childGender) {
      alert('Please select a gender.');
      return;
    }

    // Validate mobile number if provided
    if (mobileNumber && !/^[0-9]{10}$/.test(mobileNumber)) {
      setMobileError('Please enter a valid 10-digit mobile number or leave it blank');
      return;
    }
    setMobileError('');

    navigate('/mchat-assessment', {
      state: {
        childName: childName.trim(),
        childsAgeInMonths: ageNum,
        childGender,
        ...(mobileNumber && { mobileNumber }) // Only include if provided
      }
    });
    console.log('Proceeding to M-CHAT Assessment with:', { 
      childName: childName.trim(), 
      childsAgeInMonths: ageNum, 
      childGender,
      ...(mobileNumber && { mobileNumber })
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh-160px)] flex flex-col items-center justify-center bg-gradient-to-b from-sky-50 to-white dark:from-sky-900 dark:to-slate-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl w-full text-center bg-white dark:bg-slate-700 p-8 sm:p-12 rounded-xl shadow-2xl border border-sky-200 dark:border-sky-700">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-sky-700 dark:text-sky-300 mb-6">
            Free Autism Screening (M-CHAT-R/F)
          </h1>
          <div className="text-left text-gray-700 dark:text-gray-300 space-y-4 mb-8 text-base sm:text-lg">
            <p>
              The Modified Checklist for Autism in Toddlers, Revised with Follow-Up (M-CHAT-R/F) is a 2-stage parent-report screening tool to assess risk for Autism Spectrum Disorder (ASD).
            </p>
            <p>
              <span className="font-semibold">Why it matters:</span> This scientifically validated tool is designed for toddlers between <span className="font-bold">16 and 30 months old</span>. Early screening can provide valuable insights into your child's development and help identify if further evaluation is needed. Our platform offers a free, instant AI-powered summary of the results.
            </p>
            <p className="font-semibold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-800/30 p-3 rounded-md border border-amber-300 dark:border-amber-600">
              <span className="font-bold text-lg">❗ Disclaimer:</span> This screening tool is <span className="underline">not a diagnostic test</span>. It is a helpful first step to understand your child's development. Please consult with a healthcare professional for a comprehensive evaluation.
            </p>
          </div>

          <form onSubmit={handleFormSubmit} className="text-left mb-8 space-y-6 bg-sky-50 dark:bg-sky-800/20 p-6 rounded-lg border border-sky-100 dark:border-sky-700">
            <h2 className="text-xl font-semibold text-sky-800 dark:text-sky-200 mb-4 text-center">Enter Child's Details to Begin</h2>
            <div>
              <label htmlFor="childName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Child's Nickname or Full Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                id="childName"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-slate-800 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm dark:text-white"
                required
                placeholder="E.g., Alex or Alex Smith"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">This information will be kept confidential and is not shared.</p>
            </div>
            
            <div>
              <label htmlFor="childsAgeInMonths" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Child's Age (in months) <span className="text-red-500">*</span></label>
              <input
                type="number"
                id="childsAgeInMonths"
                value={childsAgeInMonths}
                onChange={(e) => {
                  setChildsAgeInMonths(e.target.value);
                  if (ageError) setAgeError('');
                }}
                className={`mt-1 block w-full px-3 py-2 border ${ageError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} dark:bg-slate-800 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm dark:text-white`}
                required
                placeholder="E.g., 18 (must be 16-30 months)"
                min="16"
                max="30"
              />
              {ageError && <p className="text-red-500 text-xs mt-1">{ageError}</p>}
            </div>

            <div>
              <label htmlFor="childGender" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Child's Gender <span className="text-red-500">*</span></label>
              <select
                id="childGender"
                value={childGender}
                onChange={(e) => setChildGender(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm dark:text-white"
                required
              >
                <option value="" disabled>Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            </div>

            <div>
              <label htmlFor="mobileNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Parent's Mobile Number (Optional)
              </label>
              <input
                type="tel"
                id="mobileNumber"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value);
                  if (mobileError) setMobileError('');
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-slate-800 rounded-md shadow-sm focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm dark:text-white"
                placeholder="10-digit mobile number (optional)"
                maxLength="10"
              />
              {mobileError && <p className="text-red-500 text-xs mt-1">{mobileError}</p>}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                We'll only use this to send you the assessment report if you can't complete it now.
              </p>
            </div>

            <button 
              type="submit" 
              className="w-full sm:w-auto mx-auto flex justify-center px-10 py-3 bg-green-500 hover:bg-green-600 text-white font-bold text-lg rounded-lg shadow-lg transition-transform transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-opacity-75"
            >
              Start Now 🚀
            </button>
          </form>

        </div>
      </div>
      <Footer />
    </>
  );
};

export default FreeAssessmentPage;
